import React, { useEffect, useState, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { sendVerificationCode } from "@/api/LoginSignup";
import "./PhoneVerificationModal.css";

const PhoneVerificationModal = ({
  isOpen,
  onClose,
  onVerify,
  phoneNumber,
  isLoading,
}) => {
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [resendMessgae, setResendMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [codeGoing, setCodeGoing] = useState(false);
  const [disableInputs, setDisableInputs] = useState(false);
  const inputRefs = useRef([]);

  const maskedPhone = (phoneNumber) => {
    if (!phoneNumber) return "";
    return `(***)***-${phoneNumber.slice(-4)}`;
  };

  useEffect(() => {
    if (!phoneNumber) return;

    const sendCode = async () => {
      try {
        setCodeGoing(true);
        setOtp(["", "", "", ""]);
        setErrorMessage("");
        setResendMessage("");
        setDisableInputs(false);

        const digitsOnly = phoneNumber.replace(/[^\d]/g, "");
        const response = await sendVerificationCode({
          phoneNumber: digitsOnly,
          verificationType: "sms",
        });

        if (response.status === "FAILURE") {
          setDisableInputs(true);
          const errorCode = response?.errorCode || "Unknown Error";

          const errorMessages = {
            PHONE_NUMBER_ALREADY_IN_USE:
              "This phone number is already verified for another user.",
            VERIFICATION_CODE_LIMIT_EXEEDED:
              "Verification Code Limit Exceeded. Try again later.",
            TECHNICAL_ERROR:
              "We're facing technical difficulties. Try again later.",
            "Invalid Request Parameters.":
              "We're facing technical difficulties. Try again later.",
          };

          const matchedError = Object.keys(errorMessages).find((key) =>
            errorCode.includes(key)
          );
          setErrorMessage(
            matchedError ? errorMessages[matchedError] : errorCode
          );
        } else {
          setDisableInputs(false);
        }
      } catch (error) {
        console.error("Verification send failed:", error);
        setDisableInputs(true);
        setErrorMessage("Something went wrong. Please try again later.");
      } finally {
        setCodeGoing(false);
      }
    };

    sendCode();
  }, [phoneNumber]);

  const handleOtpChange = (val, idx) => {
    if (!/^\d?$/.test(val)) return;
    const newOtp = [...otp];
    newOtp[idx] = val;
    setOtp(newOtp);

    if (val && idx < 3) inputRefs.current[idx + 1]?.focus();
    if (!val && idx > 0) inputRefs.current[idx - 1]?.focus();
  };

  const handleSubmit = () => {
    const fullCode = otp.join("");
    onVerify(fullCode);
  };

  const isSubmitEnabled =
    otp.every((digit) => digit !== "") &&
    !isLoading &&
    !codeGoing &&
    !disableInputs;

  const handleResend = async (verificationType = "sms") => {
    setOtp(["", "", "", ""]);
    setErrorMessage("");
    setResendMessage("");
    setDisableInputs(false);

    try {
      setCodeGoing(true);
      const digitsOnly = phoneNumber.replace(/[^\d]/g, "");

      const data = { phoneNumber: digitsOnly, verificationType };

      const response = await sendVerificationCode(data);

      if (response.status === "SUCCESS") {
        const message =
          verificationType === "call"
            ? "You will receive a call with the verification code shortly."
            : "Verification code has been resent to your phone.";
        setResendMessage(message);
        setOtp(["", "", "", ""]);
        setErrorMessage("");
      } else {
        setDisableInputs(true);

        const message =
          verificationType === "call"
            ? "Failed to initiate call. Please try again."
            : "Failed to resend code. Please try again.";
        setErrorMessage(message);
      }
    } catch (error) {
      console.log(error);
      const message =
        verificationType === "call"
          ? "Failed to initiate call. Please try again."
          : "Failed to resend code. Please try again.";
      setErrorMessage(message);
    } finally {
      setCodeGoing(false);
    }
  };

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      centered
      size="lg"
      className="phone-verify-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Phone Number Verification</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pp-phone-verify-container">
        <p className="mb-3 pp-phone-verify-text">
          Enter the verification code that has been sent to the phone number
          ending in {maskedPhone(phoneNumber)}
        </p>

        <div>
          <p>Enter the veirifcation code below:</p>
          <div className="pp-phone-verify-modal-otp-container">
            <div className="d-flex justify-content-center">
              <div className="pp-otp-container">
                {otp.map((digit, idx) => (
                  <Form.Control
                    key={idx}
                    type="text"
                    maxLength={1}
                    value={digit}
                    disabled={disableInputs}
                    ref={(el) => (inputRefs.current[idx] = el)}
                    onChange={(e) => handleOtpChange(e.target.value, idx)}
                    className="otp-box pp-otp-box"
                  />
                ))}
              </div>
            </div>

            <div className="d-flex justify-content-center">
              <Button
                variant="primary"
                onClick={handleSubmit}
                disabled={!isSubmitEnabled}
                className="pp-veryfy-modal-btn"
              >
                {isLoading ? "Verifying..." : "Submit"}
              </Button>
            </div>
          </div>
        </div>

        {resendMessgae && (
          <div className="text-success mt-2">{resendMessgae}</div>
        )}
        {errorMessage && <div className="text-danger mt-2">{errorMessage}</div>}

        {codeGoing && (
          <div className="loading-animation-container mt-3">
            <div className="loading-dots">
              <div className="loader-dot"></div>
              <div className="loader-dot"></div>
              <div className="loader-dot"></div>
            </div>
          </div>
        )}

        <div className="text-left mt-3">
          <p className="pp-resend-text help-text">
            Didn't get the code?{" "}
            <a
              className="verification-link next-navlink"
              onClick={() => handleResend("sms")}
            >
              Re-send via text
            </a>{" "}
            or{" "}
            <a
              href="#"
              className="verification-link next-navlink"
              onClick={() => handleResend("call")}
            >
              receive a call
            </a>{" "}
            instead.
          </p>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default PhoneVerificationModal;
